import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DsLoadingSpinnerComponent } from '@design-system/ds-loading-spinner/component/ds-loading-spinner.component';
import { filter, merge, Subject, take, takeUntil } from 'rxjs';
import { ServiceRequestService } from 'src/app/service/service-request.service';

import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsConfirmationModalService } from '@design-system/ds-confirmation-modal/services/ds-confirmation-modal.service';
import { DsIconButtonComponent } from '@design-system/ds-icon-button/component/ds-icon-button.component';
import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { setCategoryColor } from '@helpers/service-request-flag.helper';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { BaseModalSettingsInterface } from '@interfaces/base-modal-settings.interface';
import { NotificationService } from '@notification/service';
import { ServiceRequestFlagTabEnum } from '../enums/service-request-flag-tab.enum';
import { ServiceRequestFlag, ServiceRequestFlagPayload, ServiceRequestFlagResponse } from '../interfaces/service-request-flag.interface';
import { ServiceRequestFlagsModalDataInterface } from '../interfaces/service-request-flags-modal-data.interface';
import { FlagFormComponent } from '../modules/flag-form/component/flag-form.component';
import { FlagListComponent } from '../modules/flag-list/component/flag-list.component';
import { FlagResolveComponent } from '../modules/flag-resolve/component/flag-resolve.component';
import { SERVICE_REQUEST_FLAG_CATEGORY_OPTIONS } from '../properties/service-request-flag.properties';
import { ServiceRequestFlagFormType } from '../types/service-request-flag-form.type';

const imports = [
  NgIf,
  DsLoadingSpinnerComponent,
  DsButtonComponent,
  DsIconButtonComponent,
  DsTextComponent,
  FlagFormComponent,
  FlagListComponent,
  FlagResolveComponent,
];

const providers = [
  DsConfirmationModalService,
  { provide: DateAdapter, useClass: NativeDateAdapter },
  { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
];

@Component({
  selector: 'app-service-request-flags',
  templateUrl: './service-request-flags.component.html',
  styleUrls: ['./service-request-flags.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: imports,
  providers: providers,
})
export class ServiceRequestFlagsComponent implements OnInit, OnDestroy {
  readonly data: ServiceRequestFlagsModalDataInterface = inject(MAT_DIALOG_DATA);
  private readonly dialogRef: MatDialogRef<ServiceRequestFlagsComponent, string | undefined> = inject(MatDialogRef);
  private readonly _serviceRequestService = inject(ServiceRequestService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly notificationService = inject(NotificationService);
  private readonly dsConfirmationModalService = inject(DsConfirmationModalService);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroy$ = new Subject<void>();

  loader = true;
  isAddFlag = false;
  isUpdateFlag = false;
  isResolved = false;
  modalTitle = 'Service Request Flags';
  currentDate = new Date();
  isSaving = false;
  resolvingFlagCreatedBy = '';
  activeFlags: ServiceRequestFlag[] = [];
  resolvedFlags: ServiceRequestFlag[] = [];
  activeTab: ServiceRequestFlagTabEnum = ServiceRequestFlagTabEnum.Active;

  createServiceRequestFlagForm: ServiceRequestFlagFormType = new FormGroup({
    category: new FormControl<BaseDropdownInterface | null>(null, [Validators.required]),
    followUpDate: new FormControl(''),
    followUpTime: new FormControl('', [Validators.minLength(5)]),
    reason: new FormControl('', [Validators.required]),
  });

  resolutionNote = new FormControl('', [Validators.required]);

  serviceRequestFlagPayload: ServiceRequestFlagPayload = {
    serviceRequestId: '',
    category: '',
    reason: '',
    followUpTime: '',
    timezone: '',
    srFlagId: '',
    createdOn: '',
  };

  ngOnInit(): void {
    this.getServiceRequestFlags();
    this.syncFollowUpTimeState();
    this.createServiceRequestFlagForm.controls.followUpDate.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncFollowUpTimeState());

    // Esc and a backdrop click go through closeDialog(), so they also ask before they discard changes.
    this.dialogRef.disableClose = true;
    merge(this.dialogRef.backdropClick(), this.dialogRef.keydownEvents().pipe(filter((event) => event.key === 'Escape')))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.closeDialog());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getServiceRequestFlags(): void {
    this._serviceRequestService
      .getServiceRequestFlags(this.data.serviceRequestId)
      .pipe(take(1))
      .subscribe((serviceRequestFlags: ServiceRequestFlagResponse) => {
        const { activeFlags, resolvedFlags } = this.updateServiceRequestFlagData(serviceRequestFlags);
        this.activeFlags = this.sortByFollowUpAndCreatedOn(activeFlags || []) || [];
        this.resolvedFlags = resolvedFlags?.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime()) || [];
        this.loader = false;
        this.cdr.detectChanges();
      });
  }

  /**
   * Sorts the given list of ServiceRequestFlag objects by their follow up time and created on date.
   * Follow up time is prioritized over created on date.
   * If a follow up time is null, it is placed after all non-null follow up times.
   * If a follow up time is in the past, it is placed before all non-past follow up times.
   * @param {ServiceRequestFlag[]} serviceRequestFlags - The list of ServiceRequestFlag objects to be sorted.
   * @returns {ServiceRequestFlag[]} - The sorted list of ServiceRequestFlag objects.
   **/
  private sortByFollowUpAndCreatedOn(serviceRequestFlags: ServiceRequestFlag[]): ServiceRequestFlag[] {
    const now = new Date();
    const followUpTimePassedDueActiveFlags = serviceRequestFlags
      ?.filter((item) => item.followUpTime === null || (item.followUpTime !== null && item.followUpTime < now))
      ?.sort((a, b) => {
        if (a.followUpTime !== null && b.followUpTime !== null) {
          const aFollowUp = new Date(a.followUpTime);
          const bFollowUp = new Date(b.followUpTime);

          const aIsPast = aFollowUp < now;
          const bIsPast = bFollowUp < now;
          if (aIsPast && !bIsPast) return -1;
          if (!aIsPast && bIsPast) return 1;
        }
        return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
      });

    const followUpTimeActiveFlags = serviceRequestFlags
      ?.filter((item) => item.followUpTime !== null && item.followUpTime > now)
      ?.sort((a, b) => {
        const aFollowUp = new Date(a.followUpTime);
        const bFollowUp = new Date(b.followUpTime);
        const aIsPast = aFollowUp < now;
        const bIsPast = bFollowUp < now;
        if (aIsPast && !bIsPast) return -1;
        if (!aIsPast && bIsPast) return 1;
        return 0;
      });

    return [...followUpTimePassedDueActiveFlags, ...followUpTimeActiveFlags];
  }

  /**
   * Updates the given list of ServiceRequestFlag objects by converting their follow up time and created on date to the current user's timezone.
   * If a follow up time is null, it is not converted.
   * If a follow up time is in the past, it is marked as passed due.
   * If a follow up time is not in the past, it is marked as not passed due.
   * @param {ServiceRequestFlagResponse} serviceRequestFlags - The list of ServiceRequestFlag objects to be updated.
   * @returns {activeFlags: ServiceRequestFlag[], resolvedFlags: ServiceRequestFlag[]} - The updated list of ServiceRequestFlag objects.
   */
  private updateServiceRequestFlagData(serviceRequestFlags: ServiceRequestFlagResponse): {
    activeFlags: ServiceRequestFlag[];
    resolvedFlags: ServiceRequestFlag[];
  } {
    const activeFlags = serviceRequestFlags.activeFlags?.map((flag) => {
      const local = new Date(flag.followUpTime);
      const utcFollowUpTime = this.tranferToLocalTimeZone(local, local);
      return {
        ...flag,
        createdOn: new Date(this.tranferToLocalTimeZone(new Date(flag.createdOn), this.currentDate)),
        followUpTime: flag.followUpTime ? utcFollowUpTime : null,
        resolvedOn: new Date(this.tranferToLocalTimeZone(new Date(flag.resolvedOn), this.currentDate)),
        isPassedDue: utcFollowUpTime < this.tranferToLocalTimeZone(this.currentDate, local),
        categoryColor: setCategoryColor(flag.category),
      };
    });
    const resolvedFlags = serviceRequestFlags.resolvedFlags?.map((flag) => {
      const local = new Date(flag.followUpTime);
      const utcFollowUpTime = this.tranferToLocalTimeZone(local, local);
      return {
        ...flag,
        createdOn: new Date(this.tranferToLocalTimeZone(new Date(flag.createdOn), this.currentDate)),
        followUpTime: flag.followUpTime ? utcFollowUpTime : null,
        resolvedOn: new Date(this.tranferToLocalTimeZone(new Date(flag.resolvedOn), this.currentDate)),
        categoryColor: setCategoryColor(flag.category),
      };
    });

    return { activeFlags, resolvedFlags };
  }

  private tranferToLocalTimeZone(date: Date, currentDate: Date): Date {
    return new Date(date.getTime() - currentDate.getTimezoneOffset() * 60000);
  }

  private setModalTitle(): void {
    this.modalTitle = this.isAddFlag ? 'Create New Flag' : this.isResolved ? 'Resolve Flag' : 'Service Request Flags';
  }

  /**
   * Enables the follow up time only when a follow up date is set.
   */
  private syncFollowUpTimeState(): void {
    const { followUpDate, followUpTime } = this.createServiceRequestFlagForm.controls;
    if (followUpDate.value) {
      followUpTime.enable({ emitEvent: false });
    } else {
      followUpTime.disable({ emitEvent: false });
    }
  }

  /**
   * Moves the focus to the modal title after a view change. The screen reader then announces the new view.
   */
  private focusTitle(): void {
    this.cdr.detectChanges();
    this.elementRef.nativeElement.querySelector<HTMLElement>('#service_request_flags_title')?.focus();
  }

  /**
   * Returns true when the open form has changes that a close or a cancel discards.
   */
  private hasUnsavedChanges(): boolean {
    return (this.isAddFlag && this.createServiceRequestFlagForm.dirty) || (this.isResolved && this.resolutionNote.dirty);
  }

  /**
   * Asks the user to discard the unsaved changes. If there are no changes, runs the action at once.
   * @param action - The action to run after the user discards the changes.
   */
  private confirmDiscard(action: () => void): void {
    if (!this.hasUnsavedChanges()) {
      action();
      return;
    }
    const modalSettings: BaseModalSettingsInterface = {
      headerText: 'Discard changes?',
      bodyText: 'The changes in this form will be lost.',
      cancelText: 'Keep editing',
      confirmText: 'Discard',
    };
    this.dsConfirmationModalService
      .openModal$(modalSettings, 'warning')
      .pipe(take(1), filter(Boolean))
      .subscribe(() => action());
  }

  private splitDateTime(followUpTime: string | null, value: string): string | null {
    if (followUpTime === null) {
      return null;
    }
    if (value === 'date') {
      return new Date(followUpTime).toISOString();
    }
    const date = new Date(followUpTime);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const time = `${hours}:${minutes}`;
    return time;
  }

  private resetServiceRequestFlagPayload(): void {
    this.serviceRequestFlagPayload = {
      serviceRequestId: '',
      category: '',
      reason: '',
      followUpTime: '',
      timezone: '',
      srFlagId: '',
    };
  }

  private convertDateTime(): string | null {
    const date = this.createServiceRequestFlagForm.get('followUpDate').value;
    if (date) {
      const time = this.createServiceRequestFlagForm.get('followUpTime').value;
      const combined = new Date(date);
      if (time) {
        const [hours, minutes] = time.split(':').map(Number);
        combined.setHours(hours);
        combined.setMinutes(minutes);
        combined.setSeconds(0);
        combined.setMilliseconds(0);
      }
      return combined.toISOString();
    }
    return null;
  }

  closeDialog(): void {
    this.confirmDiscard(() => this.dialogRef.close(this.data.notificationId));
  }

  /**
   * Goes back to the flag list. If the form has unsaved changes, asks first.
   */
  onBack(): void {
    this.confirmDiscard(() => this.returnToServiceFlag());
  }

  resolveFlag(element: ServiceRequestFlagPayload): void {
    this.isResolved = true;
    this.setModalTitle();
    this.serviceRequestFlagPayload.srFlagId = element.srFlagId;
    this.serviceRequestFlagPayload.category = element.category;
    this.serviceRequestFlagPayload.followUpTime = element.followUpTime;
    this.serviceRequestFlagPayload.reason = element.reason;
    this.serviceRequestFlagPayload.timezone = element.timezone;
    this.serviceRequestFlagPayload.categoryColor = setCategoryColor(element.category);
    this.serviceRequestFlagPayload.createdOn = element.createdOn;
    this.resolvingFlagCreatedBy = (element as unknown as ServiceRequestFlag).createdBy ?? '';
    this.focusTitle();
  }

  addFlag(element?: ServiceRequestFlag): void {
    this.isAddFlag = true;
    this.isUpdateFlag = false;
    this.setModalTitle();
    if (element) {
      this.isUpdateFlag = true;
      this.updateServiceRequestFlagForm(element);
    }
    this.syncFollowUpTimeState();
    this.focusTitle();
  }

  updateServiceRequestFlagForm(element): void {
    this.serviceRequestFlagPayload.serviceRequestId = element.serviceRequestId;
    this.serviceRequestFlagPayload.srFlagId = element.srFlagId;
    this.createServiceRequestFlagForm.setValue(
      {
        category: SERVICE_REQUEST_FLAG_CATEGORY_OPTIONS.find((category) => category.name === element.category) ?? null,
        followUpDate: this.splitDateTime(element.followUpTime, 'date'),
        followUpTime: this.splitDateTime(element.followUpTime, 'time'),
        reason: element.reason,
      },
      { emitEvent: false },
    );
    // An edit changes only the follow up time.
    this.createServiceRequestFlagForm.controls.category.disable({ emitEvent: false });
    this.createServiceRequestFlagForm.controls.reason.disable({ emitEvent: false });
  }

  returnToServiceFlag(): void {
    if (this.isAddFlag) {
      this.isAddFlag = false;
      this.createServiceRequestFlagForm.reset();
      this.createServiceRequestFlagForm.controls.category.enable({ emitEvent: false });
      this.createServiceRequestFlagForm.controls.reason.enable({ emitEvent: false });
      this.createServiceRequestFlagForm.markAsUntouched();
      this.createServiceRequestFlagForm.markAsPristine();
      Object.values(this.createServiceRequestFlagForm.controls).forEach((control) => {
        control.markAsPristine();
        control.markAsUntouched();
      });
    }
    if (this.isResolved) {
      this.isResolved = false;
      this.resolutionNote.reset();
      this.resolutionNote.markAsUntouched();
    }
    this.syncFollowUpTimeState();
    this.setModalTitle();
    this.resetServiceRequestFlagPayload();
    this.focusTitle();
  }

  resolveServiceRequestFlag(): void {
    if (this.isSaving) {
      return;
    }
    if (this.resolutionNote.valid) {
      this.isSaving = true;
      this._serviceRequestService
        .resolveServiceRequestFlag({ srFlagId: this.serviceRequestFlagPayload.srFlagId, resolutionNote: this.resolutionNote.value })
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.isSaving = false;
            this.notificationService.success('Flag resolved');
            this.getServiceRequestFlags();
            this.returnToServiceFlag();
          },
          error: () => {
            this.isSaving = false;
            this.cdr.markForCheck();
          },
        });
    } else {
      this.resolutionNote.markAsTouched();
    }
  }

  saveServiceRequestFlag(): void {
    if (this.isSaving) {
      return;
    }
    if (this.createServiceRequestFlagForm.valid) {
      this.isSaving = true;
      const followUpTime = this.convertDateTime();
      if (this.serviceRequestFlagPayload.srFlagId) {
        this._serviceRequestService
          .updateServiceRequestFlagFollowupTime({ srFlagId: this.serviceRequestFlagPayload.srFlagId, followUpTime: followUpTime })
          .pipe(take(1))
          .subscribe(
            () => {
              this.isSaving = false;
              this.notificationService.success('Follow up time updated');
              this.getServiceRequestFlags();
              this.returnToServiceFlag();
            },
            ({ error }) => {
              this.isSaving = false;
              this.cdr.markForCheck();
              this.notificationService.warning(error?.errors[0] || 'Unable to update the flag follow-up time. Please try again.');
            },
          );
      } else {
        const category = this.createServiceRequestFlagForm.get('category')?.value;
        this.serviceRequestFlagPayload.followUpTime = followUpTime;
        this.serviceRequestFlagPayload.serviceRequestId = this.data.serviceRequestId;
        this.serviceRequestFlagPayload.category = category?.name;
        this.serviceRequestFlagPayload.reason = this.createServiceRequestFlagForm.get('reason')?.value;
        this.serviceRequestFlagPayload.timezone = this.data.timezone;
        this._serviceRequestService
          .createServiceRequestFlag(this.serviceRequestFlagPayload)
          .pipe(take(1))
          .subscribe(
            () => {
              this.isSaving = false;
              this.notificationService.success('Flag created');
              this.getServiceRequestFlags();
              this.returnToServiceFlag();
            },
            ({ error }) => {
              this.isSaving = false;
              this.cdr.markForCheck();
              this.notificationService.warning(error?.errors[0] || 'Unable to create the service request flag. Please try again.');
            },
          );
      }
    } else {
      this.createServiceRequestFlagForm.markAllAsTouched();
    }
  }
}
